import React, { useEffect, useMemo, useRef, useState } from "react";
import Sidebar from "../Sidebar/Sidebar";
import {
  DataGrid,
  GridActionsCellItem,
  GridToolbarContainer,
} from "@mui/x-data-grid";
import {
  Autocomplete,
  Backdrop,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Collapse,
  Divider,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/DeleteOutlined";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import AddCircleOutlineRoundedIcon from "@mui/icons-material/AddCircleOutlineRounded";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import PlaylistAddCheckRoundedIcon from "@mui/icons-material/PlaylistAddCheckRounded";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { createFilterOptions } from "@mui/material/Autocomplete";
import { Redirect } from "react-router-dom";
import { toast } from "react-toastify";
import dayjs from "dayjs";
import LocalstorageHelper from "../../LocalStorageHelper/LocalstorageHelper";

toast.configure();

const OPTIONS_LIMIT = 15;

const filterOptions = createFilterOptions({
  limit: OPTIONS_LIMIT,
  trim: true,
});

const compactInputSx = {
  "& .MuiInputBase-root": {
    minHeight: 31,
    fontSize: 12,
  },
  "& .MuiOutlinedInput-input": {
    padding: "7px 10px",
    fontSize: 12,
  },
  "& .MuiInputLabel-root": {
    fontSize: 12,
  },
};

const compactAutoSx = {
  "& .MuiInputBase-root": {
    minHeight: 31,
    fontSize: 12,
    pr: "32px !important",
  },
  "& .MuiOutlinedInput-input": {
    padding: "7px 10px !important",
    fontSize: 12,
  },
  "& .MuiInputLabel-root": {
    fontSize: 12,
  },
};

const finishCellEdit = (api, id, field) => {
  if (typeof api?.stopCellEditMode === "function") {
    api.stopCellEditMode({ id, field });
    return;
  }

  if (typeof api?.commitCellChange === "function") {
    api.commitCellChange({ id, field });
  }

  if (typeof api?.setCellMode === "function") {
    api.setCellMode(id, field, "view");
  }
};

function GridTopBar({ rowCount }) {
  return (
    <GridToolbarContainer>
      <Box
        sx={{
          px: 1,
          py: 0.5,
          width: "100%",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          bgcolor: "#f7fbff",
          borderBottom: "1px solid #dbe7f3",
        }}
      >
        <Typography sx={{ fontWeight: 700, fontSize: 11.5 }}>
          Rows: {rowCount}
        </Typography>
      </Box>
    </GridToolbarContainer>
  );
}

function PartEditAutocomplete(props) {
  const { id, field, value, api, allparts } = props;

  const selected =
    allparts.find(
      (p) =>
        String(p.itemcode || "").toUpperCase() ===
          String(value || "").toUpperCase() || p.itemid === value,
    ) || null;

  return (
    <Autocomplete
      size="small"
      options={allparts}
      value={selected}
      autoHighlight
      filterOptions={filterOptions}
      getOptionLabel={(option) =>
        option ? `${option.itemcode || ""} - ${option.itemname || ""}` : ""
      }
      isOptionEqualToValue={(option, val) => option?.itemid === val?.itemid}
      onChange={async (event, newValue) => {
        const item = newValue || null;

        await api.setEditCellValue({
          id,
          field: "itemid",
          value: item?.itemid || null,
        });

        await api.setEditCellValue({
          id,
          field,
          value: item?.itemcode || "",
        });

        await api.setEditCellValue({
          id,
          field: "itemname",
          value: item?.itemname || "",
        });

        finishCellEdit(api, id, field);
      }}
      renderOption={(props2, option) => (
        <li {...props2} key={option.itemid || option.itemcode}>
          <Box sx={{ width: "100%" }}>
            <Typography
              sx={{ fontSize: 11.5, fontWeight: 700, lineHeight: 1.15 }}
            >
              {option.itemcode} - {option.itemname}
            </Typography>
            <Typography
              sx={{ fontSize: 10.25, color: "text.secondary", lineHeight: 1.1 }}
            >
              Stock: {Number(option.closingqty || 0)} | Rack:{" "}
              {option.rackname || "N/A"}
            </Typography>
          </Box>
        </li>
      )}
      renderInput={(params) => (
        <TextField {...params} autoFocus size="small" sx={compactAutoSx} />
      )}
    />
  );
}

const WarrantyClaim = () => {
  const userToken = LocalstorageHelper.GetUserToken();

  const [disableSave, setDisableSave] = useState(false);
  const [disableUpdate, setDisableUpdate] = useState(false);

  const [claimdate, setClaimDate] = useState(dayjs());
  const [claimno, setClaimNo] = useState("");
  const [mode, setMode] = useState(1);

  const [searchdisabled, setSearchDisabled] = useState(true);
  const [searchbtndisabled, setSearchBtnDisabled] = useState(true);

  const [docopts, setDocOpts] = useState([]);
  const [docdetails, setDocDetails] = useState(null);

  const [claimremarks, setClaimRemarks] = useState("");
  const [allparts, setAllParts] = useState([]);
  const [rows, setRows] = useState([]);

  const [pageLoading, setPageLoading] = useState(false);
  const [loadingParts, setLoadingParts] = useState(false);
  const [treeLoading, setTreeLoading] = useState(false);

  const [treeSearch, setTreeSearch] = useState("");
  const [treeData, setTreeData] = useState([]);
  const [expandedDocs, setExpandedDocs] = useState({});
  const [checkedItems, setCheckedItems] = useState({});

  const [itemInput, setItemInput] = useState("");
  const [itemValue, setItemValue] = useState(null);
  const [itemsrlno, setItemSrlNo] = useState("");

  const itemRef = useRef(null);
  const srlRef = useRef(null);

  const redirectToLoginPage = () => {
    if (!userToken) {
      return <Redirect exact to="/sparepartsLogin" />;
    }
    return null;
  };

  useEffect(() => {
    const timeout = setTimeout(() => {
      LocalstorageHelper.RemoveUserToken();
    }, 3600000);
    return () => clearTimeout(timeout);
  }, []);

  const renumberRows = (arr) =>
    arr.map((row, idx) => ({
      ...row,
      srl: idx + 1,
    }));

  const normalizeRowsForGrid = (inputRows = [], parts = allparts) =>
    (Array.isArray(inputRows) ? inputRows : []).map((r, idx) => {
      const matchedItem =
        parts.find(
          (p) =>
            p.itemid === r.itemid ||
            String(p.itemcode || "").toUpperCase() ===
              String(r.itemcode || "").toUpperCase(),
        ) || null;

      return {
        srl: idx + 1,
        warrantyclaimdtlid: r.warrantyclaimdtlid || null,
        warrantyreqid: r.warrantyreqid || null,
        warrantyreqdtlid: r.warrantyreqdtlid || null,
        reqdocno: r.reqdocno || "",
        reqsrl: r.reqsrl || null,
        ticketno: r.ticketno || "",
        itemid: matchedItem?.itemid || r.itemid || null,
        itemcode: matchedItem?.itemcode || r.itemcode || "",
        itemname: matchedItem?.itemname || r.itemname || "",
        itemsrlno: r.itemsrlno || "",
        subledgerid: r.subledgerid || null,
        partydtls: r.partydtls || "",
        manualflag: !!r.manualflag,
        remarks: r.remarks || "",
      };
    });

  const resetManualEntry = () => {
    setItemInput("");
    setItemValue(null);
    setItemSrlNo("");
    setTimeout(() => {
      if (itemRef.current) itemRef.current.focus();
    }, 50);
  };

  async function getAllParts() {
    try {
      setLoadingParts(true);
      const response = await fetch("/warranty/claim/allparts", {
        method: "GET",
        headers: { sptoken: userToken },
      });
      const jsonData = await response.json();
      setAllParts(Array.isArray(jsonData) ? jsonData : []);
    } catch (err) {
      console.error(err.message);
      toast.error("Failed to load parts");
    } finally {
      setLoadingParts(false);
    }
  }

  async function getDocNos() {
    try {
      const response = await fetch("/warranty/claim/documentnos", {
        method: "GET",
        headers: { sptoken: userToken },
      });
      const jsonData = await response.json();
      setDocOpts(Array.isArray(jsonData) ? jsonData : []);
    } catch (err) {
      console.error(err.message);
    }
  }

  useEffect(() => {
    getDocNos();
    getAllParts();
  }, []);

  const handleModeChange = (e) => {
    const selectedMode = parseInt(e.target.value, 10);
    setMode(selectedMode);

    if (selectedMode === 1) {
      setSearchDisabled(true);
      setSearchBtnDisabled(true);
      setDocDetails(null);
      setClaimNo("");
      setRows([]);
      setClaimRemarks("");
      setClaimDate(dayjs());
      setTreeSearch("");
      setTreeData([]);
      setCheckedItems({});
      setExpandedDocs({});
      resetManualEntry();
    } else {
      setSearchDisabled(false);
      setSearchBtnDisabled(false);
    }
  };

  const fetchPartByCode = async (code) => {
    try {
      const body = { code: String(code || "").trim() };
      if (!body.code) return null;

      const response = await fetch("/warranty/claim/allparts/detailfetch", {
        method: "POST",
        headers: {
          sptoken: userToken,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(body),
      });

      const data = await response.json();
      if (data?.type === "error" || !data?.itemid) return null;
      return data;
    } catch (err) {
      console.error(err.message);
      return null;
    }
  };

  const resolvePartFromInput = async (typedValue, selectedPart) => {
    if (selectedPart?.itemid) return selectedPart;

    const typed = String(typedValue || "").trim();
    if (!typed) return null;

    const localMatch =
      allparts.find(
        (item) =>
          String(item.itemcode || "").toUpperCase() === typed.toUpperCase(),
      ) || null;

    if (localMatch) return localMatch;

    const fetched = await fetchPartByCode(typed);
    return fetched || null;
  };

  const handleItemBlur = async () => {
    if (itemValue?.itemid) return;

    const resolved = await resolvePartFromInput(itemInput, itemValue);
    if (resolved) {
      setItemValue(resolved);
      setItemInput(resolved.itemcode || "");
    }
  };

  const handleManualKeyDown = async (
    event,
    nextRef,
    runBlurLookup = false,
    blurFn = null,
  ) => {
    if (event.key === "Enter") {
      event.preventDefault();

      if (runBlurLookup && blurFn) {
        await blurFn();
      }

      if (nextRef?.current) {
        nextRef.current.focus();
      }
    }
  };

  const addManualRow = async () => {
    const finalItem = await resolvePartFromInput(itemInput, itemValue);

    if (!finalItem?.itemid) {
      toast.error("Select valid Item");
      return;
    }

    if (!String(itemsrlno || "").trim()) {
      toast.error("Item Serial No required");
      return;
    }

    const duplicateManual = rows.some(
      (r) =>
        r.manualflag &&
        r.itemid === finalItem.itemid &&
        String(r.itemsrlno || "")
          .trim()
          .toUpperCase() ===
          String(itemsrlno || "")
            .trim()
            .toUpperCase(),
    );

    if (duplicateManual) {
      toast.error("Same manual item + serial already added");
      return;
    }

    const newRow = {
      srl: rows.length + 1,
      warrantyclaimdtlid: null,
      warrantyreqid: null,
      warrantyreqdtlid: null,
      reqdocno: "",
      reqsrl: null,
      ticketno: "",
      itemid: finalItem.itemid,
      itemcode: finalItem.itemcode || "",
      itemname: finalItem.itemname || "",
      itemsrlno: String(itemsrlno || "").trim(),
      manualflag: true,
      remarks: "",
    };

    setRows((prev) => [...prev, newRow]);
    resetManualEntry();
  };

  const searchUnclaimed = async () => {
    setTreeLoading(true);
    try {
      const response = await fetch(
        `/warranty/claim/unclaimed/search?search=${encodeURIComponent(treeSearch || "")}`,
        {
          method: "GET",
          headers: { sptoken: userToken },
        },
      );

      const jsonData = await response.json();

      if (jsonData.type === "error") {
        toast.error(jsonData.message || "Failed to load request data");
        return;
      }

      const docs = Array.isArray(jsonData.treeData) ? jsonData.treeData : [];
      setTreeData(docs);

      const expandState = {};
      docs.forEach((d) => {
        expandState[d.warrantyreqid] = true;
      });
      setExpandedDocs(expandState);
      setCheckedItems({});
    } catch (err) {
      console.log(err.message);
      toast.error("Failed to load request data");
    } finally {
      setTreeLoading(false);
    }
  };

  const isDocExpanded = (doc) => !!expandedDocs[doc.warrantyreqid];

  const toggleDocExpand = (doc) => {
    setExpandedDocs((prev) => ({
      ...prev,
      [doc.warrantyreqid]: !prev[doc.warrantyreqid],
    }));
  };

  const getDocCheckState = (doc) => {
    const total = (doc.children || []).length;
    const checked = (doc.children || []).filter(
      (child) => checkedItems[child.warrantyreqdtlid],
    ).length;

    return {
      checked: total > 0 && checked === total,
      indeterminate: checked > 0 && checked < total,
    };
  };

  const toggleDocSelection = (doc) => {
    const { checked } = getDocCheckState(doc);

    setCheckedItems((prev) => {
      const next = { ...prev };
      (doc.children || []).forEach((child) => {
        if (checked) {
          delete next[child.warrantyreqdtlid];
        } else {
          next[child.warrantyreqdtlid] = true;
        }
      });
      return next;
    });
  };

  const toggleItemSelection = (child) => {
    setCheckedItems((prev) => {
      const next = { ...prev };
      if (next[child.warrantyreqdtlid]) {
        delete next[child.warrantyreqdtlid];
      } else {
        next[child.warrantyreqdtlid] = true;
      }
      return next;
    });
  };

  const addSelectedFromTree = () => {
    const selectedRows = [];

    treeData.forEach((doc) => {
      (doc.children || []).forEach((child) => {
        if (checkedItems[child.warrantyreqdtlid]) {
          selectedRows.push(child);
        }
      });
    });

    if (!selectedRows.length) {
      toast.error("Select document/items first");
      return;
    }

    const existingReqDtlIds = new Set(
      rows
        .filter((r) => r.warrantyreqdtlid)
        .map((r) => Number(r.warrantyreqdtlid)),
    );

    const toAdd = selectedRows.filter(
      (r) => !existingReqDtlIds.has(Number(r.warrantyreqdtlid)),
    );

    if (!toAdd.length) {
      toast.error("Selected request items already added");
      return;
    }

    const mapped = toAdd.map((r) => ({
      srl: 0,
      warrantyclaimdtlid: null,
      warrantyreqid: r.warrantyreqid || null,
      warrantyreqdtlid: r.warrantyreqdtlid || null,
      reqdocno: r.reqdocno || "",
      reqsrl: r.reqsrl || null,
      ticketno: r.ticketno || "",
      itemid: r.itemid || null,
      itemcode: r.itemcode || "",
      itemname: r.itemname || "",
      itemsrlno: r.itemsrlno || "",
      manualflag: false,
      remarks: r.remarks || "",
    }));

    setRows((prev) => renumberRows([...prev, ...mapped]));
  };

  const processRowUpdate = async (newRow) => {
    const updatedRow = { ...newRow };

    if (updatedRow.manualflag) {
      const matchedItem =
        allparts.find(
          (p) =>
            p.itemid === updatedRow.itemid ||
            String(p.itemcode || "").toUpperCase() ===
              String(updatedRow.itemcode || "").toUpperCase(),
        ) || null;

      updatedRow.itemid = matchedItem?.itemid || updatedRow.itemid || null;
      updatedRow.itemcode = matchedItem?.itemcode || updatedRow.itemcode || "";
      updatedRow.itemname = matchedItem?.itemname || updatedRow.itemname || "";
      updatedRow.itemsrlno = String(updatedRow.itemsrlno || "").trim();

      if (!updatedRow.itemid) {
        throw new Error(`Item is mandatory at row ${updatedRow.srl}`);
      }

      if (!updatedRow.itemsrlno) {
        throw new Error(`Item Serial No is mandatory at row ${updatedRow.srl}`);
      }
    }

    setRows((prev) =>
      prev.map((row) => (row.srl === updatedRow.srl ? updatedRow : row)),
    );

    return updatedRow;
  };

  const handleDeleteClick = (id) => () => {
    if (window.confirm("Remove this row?")) {
      setRows((prevRows) =>
        renumberRows(prevRows.filter((row) => row.srl !== id)),
      );
    }
  };

  const columns = useMemo(
    () => [
      {
        field: "action",
        type: "actions",
        headerName: "",
        width: 42,
        headerClassName: "super-app-theme--header",
        getActions: (params) => [
          <GridActionsCellItem
            icon={<DeleteIcon fontSize="small" />}
            label="Delete"
            onClick={handleDeleteClick(params.id)}
            color="inherit"
          />,
        ],
      },
      {
        field: "srl",
        headerName: "Srl",
        width: 55,
        headerClassName: "super-app-theme--header",
      },
      {
        field: "source",
        headerName: "Source",
        width: 90,
        headerClassName: "super-app-theme--header",
        valueGetter: (params) => (params.row.manualflag ? "MANUAL" : "REQ"),
        renderCell: (params) => (
          <Chip
            label={params.row.manualflag ? "MANUAL" : "REQ"}
            size="small"
            color={params.row.manualflag ? "warning" : "primary"}
            sx={{ height: 20, fontSize: 10.5, fontWeight: 700 }}
          />
        ),
      },
      {
        field: "reqdocno",
        headerName: "Req Doc",
        width: 120,
        headerClassName: "super-app-theme--header",
      },
      {
        field: "ticketno",
        headerName: "Ticket",
        width: 120,
        headerClassName: "super-app-theme--header",
      },
      {
        field: "itemcode",
        headerName: "Item",
        width: 300,
        headerClassName: "super-app-theme--header",
        renderCell: (params) =>
          `${params.row.itemcode || ""}${
            params.row.itemname ? ` - ${params.row.itemname}` : ""
          }`,
        renderEditCell: (params) => (
          <PartEditAutocomplete {...params} allparts={allparts} />
        ),
      },
      {
        field: "itemsrlno",
        headerName: "Item Serial No",
        width: 170,
        editable: true,
        headerClassName: "super-app-theme--header",
      },
      {
        field: "remarks",
        headerName: "Remarks",
        width: 220,
        editable: true,
        headerClassName: "super-app-theme--header",
      },
    ],
    [allparts],
  );

  const validateRows = () => {
    if (!rows.length) {
      toast.error("Add at least one row");
      return false;
    }

    for (const r of rows) {
      const matchedItem =
        r.itemid ||
        allparts.find(
          (p) =>
            String(p.itemcode || "").toUpperCase() ===
            String(r.itemcode || "").toUpperCase(),
        )?.itemid;

      if (!matchedItem) {
        toast.error(`Item missing at row ${r.srl}`);
        return false;
      }

      if (!String(r.itemsrlno || "").trim()) {
        toast.error(`Item Serial No missing at row ${r.srl}`);
        return false;
      }
    }

    return true;
  };

  const prepareRowsForSave = () => {
    return rows.map((r, idx) => {
      const matchedItem =
        allparts.find(
          (p) =>
            p.itemid === r.itemid ||
            String(p.itemcode || "").toUpperCase() ===
              String(r.itemcode || "").toUpperCase(),
        ) || null;

      return {
        ...r,
        srl: idx + 1,
        itemid: matchedItem?.itemid || r.itemid || null,
        itemcode: matchedItem?.itemcode || r.itemcode || "",
        itemname: matchedItem?.itemname || r.itemname || "",
        itemsrlno: String(r.itemsrlno || "").trim(),
        manualflag: !!r.manualflag,
      };
    });
  };

  const handleSave = async (e) => {
    e.preventDefault();

    if (!validateRows()) return;

    setDisableSave(true);
    setPageLoading(true);

    try {
      const body = {
        claimdate: claimdate ? dayjs(claimdate).format("YYYY-MM-DD") : null,
        claimremarks,
        rows: prepareRowsForSave(),
      };

      const response = await fetch("/warranty/claim/insert", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          sptoken: userToken,
        },
        body: JSON.stringify(body),
      });

      const parseRes = await response.json();

      if (parseRes.type === "success") {
        toast.success(parseRes.message, {
          autoClose: false,
          onClose: () => window.location.reload(false),
        });
      } else {
        toast.error(parseRes.message || "Save failed");
        setDisableSave(false);
      }
    } catch (err) {
      console.log(err.message);
      toast.error("Save failed");
      setDisableSave(false);
    } finally {
      setPageLoading(false);
    }
  };

  const handleSearch = async (e) => {
    e.preventDefault();

    if (!docdetails?.headerid) {
      toast.error("Select document");
      return;
    }

    setPageLoading(true);

    try {
      const response = await fetch(
        `/warranty/claim/data/${docdetails.headerid}`,
        {
          method: "GET",
          headers: { sptoken: userToken },
        },
      );

      const jsonData = await response.json();

      if (jsonData.type === "error") {
        toast.error(jsonData.message || "Search failed");
        return;
      }

      setRows(normalizeRowsForGrid(jsonData.detaildata || [], allparts));
      setClaimDate(dayjs(jsonData.headerdata.claimdate));
      setClaimNo(jsonData.headerdata.claimno || "");
      setClaimRemarks(jsonData.headerdata.claimremarks || "");
    } catch (err) {
      console.log(err.message);
      toast.error("Search failed");
    } finally {
      setPageLoading(false);
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();

    if (!docdetails?.headerid) {
      toast.error("Select document");
      return;
    }

    if (!validateRows()) return;

    setDisableUpdate(true);
    setPageLoading(true);

    try {
      const body = {
        claimdate: claimdate ? dayjs(claimdate).format("YYYY-MM-DD") : null,
        claimremarks,
        rows: prepareRowsForSave(),
      };

      const response = await fetch(
        `/warranty/claim/update/${docdetails.headerid}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            sptoken: userToken,
          },
          body: JSON.stringify(body),
        },
      );

      const parseRes = await response.json();

      if (parseRes.type === "success") {
        toast.success(parseRes.message, {
          autoClose: false,
          onClose: () => window.location.reload(false),
        });
      } else {
        toast.error(parseRes.message || "Update failed");
        setDisableUpdate(false);
      }
    } catch (err) {
      console.log(err.message);
      toast.error("Update failed");
      setDisableUpdate(false);
    } finally {
      setPageLoading(false);
    }
  };

  const isCellEditable = (params) => {
    if (params.field === "remarks") return true;
    if (
      params.row.manualflag &&
      (params.field === "itemcode" || params.field === "itemsrlno")
    ) {
      return true;
    }
    return false;
  };

  return (
    <>
      <Box>
        <Sidebar
          Title={"WARRANTY CLAIM"}
          expandedPanel={"panel2"}
          children={
            <Box
              component="form"
              sx={{
                width: "100%",
                "& .MuiCard-root": { borderRadius: 1.5 },
              }}
              autoComplete="off"
            >
              <Backdrop
                open={pageLoading || loadingParts || treeLoading}
                sx={{
                  color: "#fff",
                  zIndex: (theme) => theme.zIndex.drawer + 999,
                  backdropFilter: "blur(1.5px)",
                  flexDirection: "column",
                  gap: 1,
                }}
              >
                <CircularProgress color="inherit" size={24} />
                <Typography sx={{ fontWeight: 700, fontSize: 12 }}>
                  {loadingParts
                    ? "Loading parts..."
                    : treeLoading
                      ? "Loading request data..."
                      : "Processing..."}
                </Typography>
              </Backdrop>

              <Card elevation={1} sx={{ mb: 1 }}>
                <CardContent sx={{ p: "7px 8px !important" }}>
                  <Stack
                    direction={{ xs: "column", md: "row" }}
                    spacing={0.75}
                    alignItems={{ xs: "stretch", md: "center" }}
                    flexWrap="wrap"
                    useFlexGap
                  >
                    <TextField
                      select
                      label="Mode"
                      value={mode}
                      onChange={handleModeChange}
                      sx={{ width: 86, ...compactInputSx }}
                      size="small"
                    >
                      <MenuItem value={1}>New</MenuItem>
                      <MenuItem value={2}>Edit</MenuItem>
                    </TextField>

                    <TextField
                      disabled
                      label="Claim No"
                      value={claimno}
                      sx={{ width: 120, ...compactInputSx }}
                      size="small"
                    />

                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DatePicker
                        label="Claim Date"
                        inputFormat="DD/MM/YYYY"
                        value={claimdate}
                        onChange={(newValue) => setClaimDate(newValue)}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            size="small"
                            sx={{ width: 125, ...compactInputSx }}
                          />
                        )}
                      />
                    </LocalizationProvider>

                    <TextField
                      label="Claim Remarks"
                      value={claimremarks}
                      onChange={(e) => setClaimRemarks(e.target.value)}
                      size="small"
                      sx={{ minWidth: 180, flex: 1, ...compactInputSx }}
                    />
                  </Stack>

                  {mode === 2 && (
                    <>
                      <Divider sx={{ my: 0.8 }} />
                      <Stack
                        spacing={0.75}
                        direction={{ xs: "column", md: "row" }}
                        alignItems={{ xs: "stretch", md: "center" }}
                      >
                        <Autocomplete
                          disabled={searchdisabled}
                          id="doc-search"
                          value={docdetails}
                          filterOptions={filterOptions}
                          onChange={(event, newValue) =>
                            setDocDetails(newValue)
                          }
                          options={docopts}
                          getOptionLabel={(option) => option.documentno || ""}
                          sx={{ minWidth: 220, flex: 1, ...compactAutoSx }}
                          renderInput={(params) => (
                            <TextField
                              size="small"
                              {...params}
                              placeholder="Search Claim"
                              label="Claim Document"
                            />
                          )}
                        />

                        <Button
                          disabled={searchbtndisabled}
                          variant="contained"
                          onClick={handleSearch}
                          startIcon={<SearchRoundedIcon />}
                          sx={{
                            borderRadius: 1.2,
                            minWidth: 88,
                            height: 31,
                            fontSize: 12,
                          }}
                        >
                          Search
                        </Button>
                      </Stack>
                    </>
                  )}
                </CardContent>
              </Card>

              <Card elevation={1} sx={{ mb: 1 }}>
                <CardContent sx={{ p: "7px 8px !important" }}>
                  <Stack
                    direction={{ xs: "column", md: "row" }}
                    spacing={0.75}
                    alignItems={{ xs: "stretch", md: "center" }}
                    useFlexGap
                    flexWrap="wrap"
                  >
                    <TextField
                      label="Search Unclaimed Req"
                      size="small"
                      value={treeSearch}
                      onChange={(e) => setTreeSearch(e.target.value)}
                      sx={{ minWidth: 230, flex: 1, ...compactInputSx }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          searchUnclaimed();
                        }
                      }}
                    />

                    <Button
                      variant="contained"
                      onClick={searchUnclaimed}
                      startIcon={<SearchRoundedIcon />}
                      sx={{
                        borderRadius: 1.2,
                        minWidth: 90,
                        height: 31,
                        fontSize: 12,
                      }}
                    >
                      Search
                    </Button>

                    <Button
                      variant="contained"
                      color="success"
                      onClick={addSelectedFromTree}
                      startIcon={<PlaylistAddCheckRoundedIcon />}
                      sx={{
                        borderRadius: 1.2,
                        minWidth: 130,
                        height: 31,
                        fontSize: 12,
                      }}
                    >
                      Add Selected
                    </Button>
                  </Stack>

                  <Box
                    sx={{
                      mt: 0.8,
                      border: "1px solid #dbe7f3",
                      borderRadius: 1.2,
                      maxHeight: 300,
                      overflow: "auto",
                      bgcolor: "#fcfdff",
                    }}
                  >
                    {!treeData.length ? (
                      <Typography
                        sx={{ p: 1, fontSize: 12, color: "text.secondary" }}
                      >
                        Search to load unclaimed warranty requests.
                      </Typography>
                    ) : (
                      treeData.map((doc) => {
                        const state = getDocCheckState(doc);
                        return (
                          <Box
                            key={doc.warrantyreqid}
                            sx={{ borderBottom: "1px solid #eef3f8" }}
                          >
                            <Stack
                              direction="row"
                              alignItems="center"
                              spacing={0.4}
                              sx={{ px: 0.5, py: 0.35, bgcolor: "#f7fbff" }}
                            >
                              <Checkbox
                                size="small"
                                checked={state.checked}
                                indeterminate={state.indeterminate}
                                onChange={() => toggleDocSelection(doc)}
                              />
                              <IconButton
                                size="small"
                                onClick={() => toggleDocExpand(doc)}
                                sx={{ p: 0.35 }}
                              >
                                {isDocExpanded(doc) ? (
                                  <ExpandLessIcon fontSize="small" />
                                ) : (
                                  <ExpandMoreIcon fontSize="small" />
                                )}
                              </IconButton>

                              <Typography
                                sx={{ fontSize: 11.5, fontWeight: 800 }}
                              >
                                {doc.docno}
                              </Typography>
                              <Chip
                                label={`Items ${doc.itemcount || 0}`}
                                size="small"
                                sx={{ height: 20, fontSize: 10.5 }}
                              />
                              <Typography
                                sx={{ fontSize: 11, color: "text.secondary" }}
                              >
                                {doc.docdate
                                  ? dayjs(doc.docdate).format("DD/MM/YYYY")
                                  : ""}
                              </Typography>
                            </Stack>

                            <Collapse
                              in={isDocExpanded(doc)}
                              timeout="auto"
                              unmountOnExit
                            >
                              <Box sx={{ px: 0.5, pb: 0.4 }}>
                                {(doc.children || []).map((child) => (
                                  <Stack
                                    key={child.warrantyreqdtlid}
                                    direction="row"
                                    alignItems="flex-start"
                                    spacing={0.5}
                                    sx={{
                                      pl: 4.5,
                                      py: 0.35,
                                      borderRadius: 0.8,
                                      "&:hover": { bgcolor: "#f8fbff" },
                                    }}
                                  >
                                    <Checkbox
                                      size="small"
                                      checked={
                                        !!checkedItems[child.warrantyreqdtlid]
                                      }
                                      onChange={() =>
                                        toggleItemSelection(child)
                                      }
                                    />

                                    <Typography
                                      sx={{ width: 42, fontSize: 11, pt: 0.2 }}
                                    >
                                      {child.reqsrl}
                                    </Typography>

                                    <Box sx={{ minWidth: 90 }}>
                                      <Typography sx={{ fontSize: 11 }}>
                                        {child.ticketno || "-"}
                                      </Typography>
                                    </Box>

                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                      <Typography
                                        sx={{
                                          fontSize: 11.2,
                                          fontWeight: 600,
                                          lineHeight: 1.15,
                                        }}
                                      >
                                        {child.itemcode}{" "}
                                        {child.itemname
                                          ? `- ${child.itemname}`
                                          : ""}
                                      </Typography>
                                      {!!child.partydtls && (
                                        <Typography
                                          sx={{
                                            fontSize: 10.25,
                                            color: "text.secondary",
                                            lineHeight: 1.1,
                                          }}
                                        >
                                          {child.partydtls}
                                        </Typography>
                                      )}
                                    </Box>

                                    <Typography
                                      sx={{
                                        minWidth: 120,
                                        fontSize: 11,
                                        pt: 0.2,
                                      }}
                                    >
                                      {child.itemsrlno || "-"}
                                    </Typography>
                                  </Stack>
                                ))}
                              </Box>
                            </Collapse>
                          </Box>
                        );
                      })
                    )}
                  </Box>
                </CardContent>
              </Card>

              <Card elevation={1} sx={{ mb: 1 }}>
                <CardContent sx={{ p: "7px 8px !important" }}>
                  <Stack
                    direction="row"
                    spacing={0.65}
                    alignItems="center"
                    flexWrap="wrap"
                    useFlexGap
                  >
                    <Autocomplete
                      freeSolo
                      value={itemValue}
                      inputValue={itemInput}
                      filterOptions={filterOptions}
                      onInputChange={(event, newInputValue) => {
                        setItemInput(newInputValue);
                        if (!newInputValue) setItemValue(null);
                      }}
                      onChange={(event, newValue) => {
                        if (typeof newValue === "string") {
                          setItemValue(null);
                          setItemInput(newValue);
                        } else {
                          setItemValue(newValue);
                          setItemInput(newValue?.itemcode || "");
                        }
                      }}
                      options={allparts}
                      loading={loadingParts}
                      getOptionLabel={(option) => {
                        if (typeof option === "string") return option;
                        return option?.itemcode
                          ? `${option.itemcode} - ${option.itemname || ""}`
                          : "";
                      }}
                      isOptionEqualToValue={(option, value) =>
                        option?.itemid === value?.itemid
                      }
                      sx={{ width: 270, ...compactAutoSx }}
                      renderOption={(props, option) => (
                        <li {...props} key={option.itemid || option.itemcode}>
                          <Box sx={{ width: "100%" }}>
                            <Typography
                              sx={{
                                fontSize: 11.5,
                                fontWeight: 700,
                                lineHeight: 1.15,
                              }}
                            >
                              {option.itemcode} - {option.itemname}
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: 10.25,
                                color: "text.secondary",
                                lineHeight: 1.1,
                              }}
                            >
                              Stock: {Number(option.closingqty || 0)} | Rack:{" "}
                              {option.rackname || "N/A"}
                            </Typography>
                          </Box>
                        </li>
                      )}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Manual Item"
                          size="small"
                          inputRef={itemRef}
                          onBlur={handleItemBlur}
                          onKeyDown={(e) =>
                            handleManualKeyDown(e, srlRef, true, handleItemBlur)
                          }
                        />
                      )}
                    />

                    <TextField
                      label="Manual Item Srl No"
                      size="small"
                      value={itemsrlno}
                      onChange={(e) => setItemSrlNo(e.target.value)}
                      inputRef={srlRef}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          addManualRow();
                        }
                      }}
                      sx={{ width: 170, ...compactInputSx }}
                    />

                    <Button
                      size="small"
                      variant="contained"
                      color="primary"
                      startIcon={<AddCircleOutlineRoundedIcon />}
                      onClick={addManualRow}
                      sx={{
                        borderRadius: 1.2,
                        px: 1,
                        height: 31,
                        minWidth: 88,
                        fontSize: 11.5,
                      }}
                    >
                      Add Manual
                    </Button>
                  </Stack>
                </CardContent>
              </Card>

              <Card elevation={1} sx={{ mb: 1 }}>
                <CardContent sx={{ p: 0 }}>
                  <Box
                    sx={{
                      height: 410,
                      width: "100%",
                      "& .super-app-theme--header": {
                        backgroundColor: "#ebf5ff",
                        color: "#0d3557",
                        fontWeight: 800,
                        fontSize: 11.25,
                      },
                      "& .MuiDataGrid-cell": {
                        fontSize: 11.25,
                        py: 0,
                      },
                      "& .MuiDataGrid-footerContainer": {
                        minHeight: 36,
                      },
                    }}
                  >
                    <DataGrid
                      rows={rows}
                      columns={columns}
                      getRowId={(row) => row.srl}
                      density="compact"
                      rowHeight={27}
                      headerHeight={29}
                      disableSelectionOnClick
                      editMode="cell"
                      experimentalFeatures={{ newEditingApi: true }}
                      processRowUpdate={processRowUpdate}
                      isCellEditable={isCellEditable}
                      onProcessRowUpdateError={(error) => {
                        console.error(error);
                        toast.error(error?.message || "Failed to update row");
                      }}
                      components={{
                        Toolbar: () => <GridTopBar rowCount={rows.length} />,
                      }}
                      initialState={{
                        sorting: {
                          sortModel: [{ field: "srl", sort: "asc" }],
                        },
                        pagination: {
                          pageSize: 10,
                        },
                      }}
                      rowsPerPageOptions={[10, 20, 50]}
                    />
                  </Box>
                </CardContent>
              </Card>

              <Stack direction="row" spacing={1} justifyContent="center">
                {mode === 1 && (
                  <Button
                    disabled={disableSave}
                    onClick={handleSave}
                    size="small"
                    variant="contained"
                    color="primary"
                    sx={{
                      borderRadius: 1.2,
                      minWidth: 90,
                      height: 31,
                      fontSize: 12,
                    }}
                  >
                    Save
                  </Button>
                )}

                {mode === 2 && (
                  <Button
                    disabled={disableUpdate}
                    onClick={handleUpdate}
                    size="small"
                    variant="contained"
                    color="warning"
                    sx={{
                      borderRadius: 1.2,
                      minWidth: 90,
                      height: 31,
                      fontSize: 12,
                    }}
                  >
                    Update
                  </Button>
                )}
              </Stack>
            </Box>
          }
        />
        {redirectToLoginPage()}
      </Box>
    </>
  );
};

export default WarrantyClaim;
